/**
 * GREAT WORKS CATEGORIES - Biome groupings for flexible requirements
 *
 * Categories allow Great Works to accept multiple biome types,
 * making them achievable with random tile generation.
 */

// Biome category definitions
export const BIOME_CATEGORIES = {
  nature: {
    id: 'nature',
    name: 'Nature',
    icon: '🌿',
    description: 'Lush, living biomes with flora and fauna',
    biomes: [
      'forest', 'plains', 'grassland', 'swamp', 'meadow', 'bambooGrove',
      'savanna', 'riverDelta', 'oasis', 'primordialJungle', 'livingForest',
      'mysticGrove', 'worldTreeRoot'
    ],
  },

  stone: {
    id: 'stone',
    name: 'Stone',
    icon: '⛰️',
    description: 'Rocky terrain with mineral deposits',
    biomes: [
      'mountains', 'rockyOutcrop', 'volcanic', 'crystalCaves', 'obsidianFields',
      'windsweptPeaks', 'petrifiedForest', 'saltFlats', 'sulfurSprings',
      'echoingCanyon'
    ],
  },

  knowledge: {
    id: 'knowledge',
    name: 'Knowledge',
    icon: '📜',
    description: 'Places of ancient wisdom and learning',
    biomes: [
      'ruins', 'ancientRuins', 'overgrownShrine', 'celestialObservatory',
      'sunkenCity', 'hauntedBattlefield', 'timeWornValley', 'forgottenGod'
    ],
  },

  water: {
    id: 'water',
    name: 'Water',
    icon: '🌊',
    description: 'Coastal and aquatic biomes',
    biomes: [
      'coastal', 'shallowWaters', 'shipwreckCove', 'coralReef', 'riverDelta',
      'abyssalTrench', 'oasis'
    ],
  },

  magical: {
    id: 'magical',
    name: 'Magical',
    icon: '✨',
    description: 'Biomes infused with arcane energy',
    biomes: [
      'mysticGrove', 'voidRift', 'etherealMarsh', 'elementalConfluence',
      'dreamscape', 'shadowRealm', 'astralPlane', 'primordialChaos',
      'heavensGate', 'nightmareRealm', 'teleportBeacon'
    ],
  },

  hostile: {
    id: 'hostile',
    name: 'Hostile',
    icon: '💀',
    description: 'Dangerous and treacherous terrain',
    biomes: [
      'volcanic', 'thornlands', 'barrenWastes', 'frozenTundra', 'deadForest',
      'hauntedBattlefield', 'dragonsLair', 'nightmareRealm', 'boneyard'
    ],
  },

  exotic: {
    id: 'exotic',
    name: 'Exotic',
    icon: '🦋',
    description: 'Rare and unusual biomes',
    biomes: [
      'mushroomCavern', 'crystalCaves', 'dragonGraveyard', 'titanRest',
      'phoenixNest', 'giantHive', 'mirageCity', 'worldTreeRoot'
    ],
  },
};

// Pre-compute reverse lookup: biome -> categories
const biomeToCategories = {};
for (const [catId, category] of Object.entries(BIOME_CATEGORIES)) {
  for (const biomeId of category.biomes) {
    if (!biomeToCategories[biomeId]) {
      biomeToCategories[biomeId] = [];
    }
    biomeToCategories[biomeId].push(catId);
  }
}

/**
 * Get all categories a biome belongs to
 * @param {string} biomeId
 * @returns {string[]} Array of category IDs
 */
export function getBiomeCategories(biomeId) {
  return biomeToCategories[biomeId] || [];
}

/**
 * Check if a biome belongs to a category
 * @param {string} biomeId
 * @param {string} categoryId
 * @returns {boolean}
 */
export function biomeMatchesCategory(biomeId, categoryId) {
  const category = BIOME_CATEGORIES[categoryId];
  return category ? category.biomes.includes(biomeId) : false;
}

/**
 * Check if a biome matches a requirement (category or specific biome)
 * @param {string} biomeId - The biome to check
 * @param {string|string[]} requirement - Category ID, biome ID, or array of either
 * @returns {boolean}
 */
export function biomeMatchesRequirement(biomeId, requirement) {
  if (!requirement) return true;

  // Handle array of requirements (OR logic)
  if (Array.isArray(requirement)) {
    return requirement.some(req => biomeMatchesRequirement(biomeId, req));
  }

  // Check if it's a category
  if (BIOME_CATEGORIES[requirement]) {
    return biomeMatchesCategory(biomeId, requirement);
  }

  // Direct biome match
  return biomeId === requirement;
}

/**
 * Get all biomes that match a requirement
 * @param {string|string[]} requirement - Category ID, biome ID, or array
 * @returns {string[]} Array of matching biome IDs
 */
export function getBiomesMatchingRequirement(requirement) {
  if (!requirement) return [];

  if (Array.isArray(requirement)) {
    const result = new Set();
    for (const req of requirement) {
      for (const biome of getBiomesMatchingRequirement(req)) {
        result.add(biome);
      }
    }
    return [...result];
  }

  // Category
  if (BIOME_CATEGORIES[requirement]) {
    return [...BIOME_CATEGORIES[requirement].biomes];
  }

  // Single biome
  return [requirement];
}

export default BIOME_CATEGORIES;
